import { Component } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import {UserService} from '../../../../../../../chat-common/src/lib/services/user.service';
import {UserInfoService} from '../../../../../../../chat-common/src/lib/services/user.info.service';
import {UserProfile, UserUnits} from '../../../../../../../chat-common/src/lib/models/user';


@Component({
  selector: 'app-profile',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './profile.component.html',
  styleUrl: './profile.component.scss'
})
export class ProfileComponent {
  profile: UserProfile = new UserProfile();
  userUnits: UserUnits = new UserUnits();
  weight: number;
  height: number;
  isSaving = false;
  // Conversion constants
  private readonly kgPerLb: number = 0.45359237; // kilograms in one pound
  private readonly lbsPerKg: number = 2.2046226218; // pounds in one kilogram
  private inchCoef: number = 2.54;
  // View-model for weight input shown in the current unit (kg or lbs)
  weightInput: number = 0;
  heightValueImperialInputFt: number = 0;
  heightValueImperialInputIn: number = 0;
  constructor(private userInfo: UserInfoService, private userService: UserService) {}

  async ngOnInit() {
    const existing = await this.userInfo.getUserProfile();
    if (existing) {
      this.profile = { ...this.profile, ...existing };
    } else {
      // fallback from old realtime DB if exists
      const anyProfile: any = this.userService.user?.profile;
      if (anyProfile) {
        this.profile = { ...this.profile, ...anyProfile } as UserProfile;
      }

    }

    const units = await this.userInfo.getUserUnits();
    if (units) this.userUnits = { ...this.userUnits, ...units };
    this.recalculate();
  }

  async setUnit<K extends keyof UserUnits>(key: K, value: UserUnits[K]) {
    // update local state
    this.userUnits = { ...this.userUnits, [key]: value } as UserUnits;
    this.recalculate();
    // save immediately
    await this.userInfo.saveUserUnits({ [key]: value } as Partial<UserUnits>);
  }

  recalculate(){
    const weightKg = Number(this.profile.weightInKg) || 0;
    if (this.userUnits.massMetric) {
      // Display whole kilograms (no decimals)
      this.weight = Math.round(weightKg);
      this.weightInput = this.weight;
    } else {
      // Display whole pounds (no decimals)
      const lbs = weightKg * this.lbsPerKg;
      this.weight = Math.round(lbs);
      this.weightInput = this.weight;
    }

    if(this.userUnits.lengthMetric){
      this.height = this.profile.heightInCm;
    } else {
      const inchesTotal = (this.profile?.heightInCm ?? 0) / this.inchCoef;
      this.height = Math.round(inchesTotal * 10) / 10;
      this.heightValueImperialInputFt = Math.max(0, Math.floor(this.height / 12));
      this.heightValueImperialInputIn = Math.max(0, Math.round(this.height % 12));
    }

  }

  onImperialHeightChange() {
    // Normalize values and update profile.heightInCm from ft/in
    let ft = Number(this.heightValueImperialInputFt) || 0;
    let inch = Number(this.heightValueImperialInputIn) || 0;

    // Carry over inches >= 12 and handle negatives gracefully
    if (inch >= 12) {
      ft += Math.floor(inch / 12);
      inch = inch % 12;
    }
    if (inch < 0) {
      const borrow = Math.ceil(Math.abs(inch) / 12);
      ft = Math.max(0, ft - borrow);
      inch = (borrow * 12 + inch); // inch is negative, so this reduces it
      if (inch < 0) inch = 0;
    }
    if (ft < 0) ft = 0;

    this.heightValueImperialInputFt = ft;
    this.heightValueImperialInputIn = inch;

    const totalInches = ft * 12 + inch;
    const cm = totalInches * this.inchCoef;
    this.profile.heightInCm = Math.round(cm * 10) / 10; // keep one decimal precision
  }

  onWeightInputChange() {
    // Normalize and update canonical kg value based on current unit
    let val = Number(this.weightInput);
    if (!isFinite(val) || isNaN(val)) val = 0;
    if (val < 0) val = 0;

    // Enforce whole numbers for both kg and lbs inputs
    val = Math.round(val);
    this.weightInput = val;

    if (this.userUnits.massMetric) {
      // Input is kg (store as whole kilograms)
      this.profile.weightInKg = val;
    } else {
      // Input is lbs (convert to kg, keep internal precision to 0.01 kg)
      const kg = val * this.kgPerLb;
      this.profile.weightInKg = Math.round(kg * 100) / 100;
    }
  }

  async save() {
    if (this.isSaving) return; // prevent duplicate saves
    this.isSaving = true;
    try {
      // Persist relevant fields
      await this.userInfo.saveUserName(this.profile.name || null);
      await this.userInfo.saveUserDateOfBirth(this.profile.dateOfBirth || null);
      // Save height/weight and other profile attributes
      await this.userService.saveProfile({
        name: this.profile.name ?? null,
        dateOfBirth: this.profile.dateOfBirth ?? null,
        heightInCm: this.profile.heightInCm ?? null,
        weightInKg: this.profile.weightInKg ?? null,
        weightMeasurementDate: this.profile.weightMeasurementDate ?? null,
        userCreated: this.profile.userCreated ?? null,
      });
    } finally {
      this.isSaving = false;
    }
  }
}
